
      const load_time = new Date().getTime();
      const backend = new URL(import.meta.env.VITE_APP_API_ENDPOINT, document.location).origin;

      // Which tenant's branding to ask for.
      //
      // The theme is fetched HERE, before React and long before anybody signs in, so the
      // server cannot work the tenant out from a logged-in user — it has to be told. The
      // slug is in the address on a tenant entry point (`/t/<slug>`), and it is remembered
      // because the address stops carrying it the moment the person signs in and lands on
      // their tray: without that, the app would be branded correctly on the login screen and
      // then revert to someone else's colours one navigation later.
      //
      // sessionStorage, not localStorage: two tenants opened in two tabs must not fight over
      // one value, and a closed tab should not decide how the next visit looks.
      const TENANT_KEY = 'uwe_tenant_entry';
      const fromPath = /^\/t\/([^/?#]+)/.exec(window.location.pathname);
      // `?tenant=` as well as the path, because a deep link from the Studio ("Go to
      // Project" pointed at a specific case) cannot also be a `/t/<slug>` entry point —
      // one is a door, the other is a destination — so the tenant travels as a parameter
      // on those.
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('tenant');

      // And inside `redirectTo`, which is where `/process-login` keeps the destination.
      // Arriving with a ticket from the Studio, the address bar says `/process-login` and
      // nothing else: a tenant named only by the destination would be invisible right here,
      // the hostname below would answer "no tenant" — it is the deployment's, shared by all
      // of them — and the whole session would be spent in somebody else's colours.
      const fromDestination = (() => {
        const destination = params.get('redirectTo');
        if (!destination) return null;
        try {
          // The base only has to parse; the destination is relative and never reads it back.
          const inside = new URL(destination, window.location.origin);
          const door = /^\/t\/([^/?#]+)/.exec(inside.pathname);
          return door ? decodeURIComponent(door[1]) : inside.searchParams.get('tenant');
        } catch (e) {
          return null;
        }
      })();

      // A PUBLIC FORM names its tenant by ID, in the path: `/anonymous/<id>/…`. It is the one
      // entry point that carries no slug anywhere, so without reading it here a tenant's own
      // applicants meet the deployment's colours on the first screen they ever see.
      //
      // Deliberately NOT remembered as the tenant entry: an id is not a slug, and storing it
      // would make `/t/<id>` this tab's sign-in door and resolve to nothing. It paints this
      // page and goes no further.
      const fromPublicForm = /^\/anonymous\/(\d+)(?:\/|$)/.exec(window.location.pathname);

      let tenant = fromPath
        ? decodeURIComponent(fromPath[1])
        : (fromQuery || fromDestination || null);

      try {
        if (tenant) sessionStorage.setItem(TENANT_KEY, tenant);
        else tenant = sessionStorage.getItem(TENANT_KEY);
      } catch (e) {
        // Private windows and blocked site data throw here. The deployment-wide theme is a
        // perfectly good answer, so carry on unbranded rather than failing to paint at all.
      }

      // Still nothing? Then this may be a tenant's OWN address, which carries no slug
      // anywhere: no `/t/` segment, no parameter, and an empty sessionStorage on a hostname
      // visited for the first time.
      //
      // Asking the server costs one round trip, and it is paid BEFORE the favicon and the
      // theme on purpose. Those two are a `<link>` and a `fetch`, and only the fetch carries
      // an `Origin` the server could resolve the tenant from — a `<link>` carries none, and
      // this site's `same-origin` referrer policy strips the `Referer` on a cross-origin
      // request. So the server cannot work it out for the favicon no matter what it does: the
      // slug has to be in the URL, which means knowing it here.
      //
      // The empty string is a real answer and is stored as one: it means "already asked, this
      // host belongs to no tenant", so the plain entry point pays this round trip once per
      // session instead of on every page.
      // A public form already knows which tenant it belongs to, by id, so it skips the round
      // trip rather than asking a hostname that every tenant of the deployment shares.
      if (tenant === null && !fromPublicForm) {
        try {
          const response = await fetch(
            `${backend}/api/tenants/by-host?host=${encodeURIComponent(window.location.hostname)}`
          );
          tenant = response.ok ? ((await response.json())?.data?.slug || '') : '';
        } catch (e) {
          tenant = '';
        }

        try { sessionStorage.setItem(TENANT_KEY, tenant); } catch (e) { /* see above */ }
      }

      // What this page is painted with. The public form's id wins here and only here: the
      // branding endpoints accept either spelling, and the id is the only thing that address
      // carries. Everything else in the app keeps using the slug.
      const paintWith = fromPublicForm ? fromPublicForm[1] : tenant;
      const tenantParam = paintWith ? `&tenant=${encodeURIComponent(paintWith)}` : '';

      (() => {
        const link = document.createElement('link');
        link.rel = 'shortcut icon';
        link.href = `${backend}/template-files/favicon.ico?lt=${load_time}${tenantParam}`;
        document.head.append(link);
      })();

      window.uwe_template = fetch(`${backend}/template-files/style.json?lt=${load_time}${tenantParam}`).then(r => r.json());
    